import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';

interface Payment {
  id: string;
  order_number: string;
  company_name: string;
  total_amount: number;
  payment_amount: number;
  deposit_paid: boolean;
  paid_in_full: boolean;
  payment_status: string;
  payment_method: string | null;
  payment_reference: string | null;
  effective_payment_date: string;
  payment_date: string | null;
  submitted_at: string | null;
}

const METHOD_LABELS: Record<string, { label: string; color: string }> = {
  stripe_card: { label: 'Credit Card', color: 'bg-purple-100 text-purple-800' },
  stripe_ach: { label: 'ACH', color: 'bg-indigo-100 text-indigo-800' },
  authorize_net: { label: 'Authorize.net', color: 'bg-amber-100 text-amber-800' },
  helcim_ach: { label: 'Helcim ACH', color: 'bg-teal-100 text-teal-800' },
  check: { label: 'Check', color: 'bg-amber-100 text-amber-800' },
  wire_transfer: { label: 'Wire', color: 'bg-cyan-100 text-cyan-800' },
  cash: { label: 'Cash', color: 'bg-green-100 text-green-800' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-800' },
};

export default function RevenuePaymentsPage() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState({
    deposits_count: 0,
    paid_in_full_count: 0,
    total_paid_in_full: 0,
    total_deposits: 0,
  });

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  useEffect(() => {
    fetchPayments();
  }, [month, year]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await api.accounting.getRevenuePayments({ month, year });
      setPayments(data.items || []);
      setTotals(data.totals || {
        deposits_count: 0,
        paid_in_full_count: 0,
        total_paid_in_full: 0,
        total_deposits: 0,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Number(amount) || 0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const totalReceived = Number(totals.total_paid_in_full || 0) + Number(totals.total_deposits || 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payments Received</h1>
        <p className="text-gray-500 mt-1">Deposits and payments received by payment date</p>
      </div>

      {/* Filters & Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                {months.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                {[2024, 2025, 2026, 2027, 2028].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 text-right">
            <div>
              <p className="text-sm text-gray-500">Deposits</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(totals.total_deposits)}</p>
              <p className="text-xs text-gray-400">{totals.deposits_count} orders</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Paid in Full</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totals.total_paid_in_full)}</p>
              <p className="text-xs text-gray-400">{totals.paid_in_full_count} orders</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Received</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalReceived)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Payments Table */}
      {!loading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Method
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Order Total
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Amount Paid
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment) => {
                const method = payment.payment_method ? METHOD_LABELS[payment.payment_method] : null;
                return (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/orders/${payment.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        {payment.order_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                      {payment.company_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(payment.effective_payment_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {payment.paid_in_full ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Paid in Full
                        </span>
                      ) : payment.deposit_paid ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          Deposit
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {method ? (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${method.color}`}>
                          {method.label}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                      {payment.payment_reference && payment.payment_reference !== 'undefined' && payment.payment_reference !== 'unknown' && (
                        <p className="text-xs text-gray-400 mt-0.5">#{payment.payment_reference}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                      {formatCurrency(payment.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                      {formatCurrency(payment.payment_amount)}
                    </td>
                  </tr>
                );
              })}
              {payments.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No payments received for this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
